import { useQuery } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./services/firebase";
import { useAppState } from "./context/AppContext";

/**
 * The signed-in user's profile (users/{uid}: { email, name }).
 *
 * Read from Firestore rather than off state.user, which is the bare Firebase
 * auth user once a protected route mounts (see PrivateRoute) and carries no
 * name of its own.
 *
 * @returns {{ name: string, email: string, isLoading: boolean }} `name` falls
 *   back to the account email when the profile has no name on it, so callers
 *   always have something to attribute a case to.
 */
export function useUserProfile() {
  const { state } = useAppState();
  const uid = state.user?.uid;
  const email = state.user?.email || "";

  const { data, isLoading } = useQuery({
    queryKey: ["user-profile", uid],
    queryFn: async () => {
      const snap = await getDoc(doc(db, "users", uid));
      return snap.exists() ? snap.data() : {};
    },
    enabled: !!uid,
    staleTime: 1000 * 60 * 5,
  });

  return {
    name: data?.name || email,
    email: data?.email || email,
    isLoading: uid ? isLoading : false,
  };
}
